import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import Checkbox from "@cloudscape-design/components/checkbox";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Grid from "@cloudscape-design/components/grid";
import DatePicker from "@cloudscape-design/components/date-picker";
import Toggle from "@cloudscape-design/components/toggle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { bannersEditModalStore, BannersEditModalStore, ALLOWED_EXTERNAL_HOSTS, MAX_BANNER_BYTES, normalizeRequireAuthValue } from "../stores/bannersEditModal";
import { bannersStore, BannersStore } from "../stores/banners";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";

const isAllowedExternalBannerLink = (rawValue) => {
	try {
		return ALLOWED_EXTERNAL_HOSTS.includes(rawValue);
	} catch (error) {
		return false;
	}
};

const isValidBannerFileSize = (file) => Boolean(file) && file.size <= MAX_BANNER_BYTES;

class BannersEditModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isDirty: false,
			initialValues: cloneDeep(props.bannersEditModal),
		};
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.bannersEditModal.show && this.props.bannersEditModal.show) {
			const normalizedRequireAuth = normalizeRequireAuthValue(this.props.bannersEditModal.requireAuth);

			if (normalizedRequireAuth !== this.props.bannersEditModal.requireAuth) {
				this.updateParam("requireAuth", normalizedRequireAuth);
				this.setState({ initialValues: cloneDeep({ ...this.props.bannersEditModal, requireAuth: normalizedRequireAuth }), isDirty: false });

				return;
			}

			this.setState({ initialValues: cloneDeep(this.props.bannersEditModal), isDirty: false });
		}
	}

	editBanner() {
		if (this.props.bannersEditModal.linkType?.value === "external" && !isAllowedExternalBannerLink(this.props.bannersEditModal.link)) {
			this.props.Interface.addNoty("Error", "External links are allowed only for vk.com, tg.me, or telegram.me");

			return;
		}

		const { requireTg, requireEmail, ...bannerPayload } = this.props.bannersEditModal;
		const payload = {
			...bannerPayload,
			skipLinkTg: requireTg ? 1 : 0,
			skipLinkEmail: requireEmail ? 1 : 0,
		};
		this.props.Interface.request(
			"set",
			{
				class: "data",
				module: "banners",
				method: "edit",
			},
			payload,
		)
			.then((result) => {
				this.props.Interface.addNoty("Success", "Banner was successfully updated");
				this.props.setBannersEditModal({ ...this.props.bannersEditModal, show: false });
				this.props.Interface.loadBannerList();
			})
			.catch((error) => {
				if (typeof error.text === "undefined") error = { text: error.toString() };
				this.props.setBanners({ ...this.props.banners, loaded: true, error });
			});
	}

	updateParam = (key, value) => {
		const updatedState = cloneDeep(this.props.bannersEditModal);
		updatedState[key] = value;
		this.props.setBannersEditModal(updatedState);

		const isDirty = Object.keys(updatedState).some((param) => updatedState[param] !== this.state.initialValues[param]);
		this.setState({ isDirty });
	};

	render() {
		const { bannersEditModal, setBannersEditModal } = this.props;
		const { isDirty } = this.state;

		return (
			<Modal
				visible={bannersEditModal.show}
				onDismiss={() => setBannersEditModal({ ...bannersEditModal, show: false })}
				header="Edit Banner"
				children={
					<SpaceBetween direction="vertical" size="m">
						<Box variant="span">Title Type</Box>
						<Select filteringType="auto" placeholder="Select title type" selectedOption={bannersEditModal.titleType} onChange={(option) => this.updateParam("titleType", option.detail.selectedOption)} options={bannersEditModal.currentOption.titleType} />
						<Box variant="span">Title</Box>
						<Input name="Title" autoComplete="off" inputMode="text" type="text" disableBrowserAutocorrect={true} placeholder="Title" value={bannersEditModal.title} onChange={(event) => this.updateParam("title", event.detail.value)} />
						<Box variant="span">Image</Box>
						<input
							type="file"
							accept="image/png"
							onChange={(event) => {
								const file = event.target.files[0];
								if (file) {
									if (!isValidBannerFileSize(file)) {
										this.props.Interface.addNoty("Error", `Image size must not exceed ${MAX_BANNER_BYTES / 1024 / 1024} MB`);
										event.target.value = "";

										return;
									}

									const reader = new FileReader();
									reader.onload = (event) => {
										const result = event.target.result;
										this.updateParam("image", result);
									};
									reader.readAsDataURL(file);
								} else {
									this.props.Interface.addNoty("Error", "Please, select a valid image file");
								}
							}}
						/>
						<Box variant="span">Button Text Type</Box>
						<Select filteringType="auto" placeholder="Select button type" selectedOption={bannersEditModal.buttonTextType} onChange={(option) => this.updateParam("buttonTextType", option.detail.selectedOption)} options={bannersEditModal.currentOption.buttonTextType} />
						<Box variant="span">Button Type</Box>
						<Select filteringType="auto" placeholder="Select button type" selectedOption={bannersEditModal.buttonType} onChange={(option) => this.updateParam("buttonType", option.detail.selectedOption)} options={bannersEditModal.currentOption.buttonType} />
						<Box variant="span">Button Text</Box>
						<Input name="ButtonText" autoComplete="off" inputMode="text" type="text" disableBrowserAutocorrect={true} placeholder="Button Text" value={bannersEditModal.buttonText} onChange={(event) => this.updateParam("buttonText", event.detail.value)} />
						<Box variant="span">Link Type</Box>
						<Select filteringType="auto" placeholder="Select link type" selectedOption={bannersEditModal.linkType} onChange={(option) => this.updateParam("linkType", option.detail.selectedOption)} options={bannersEditModal.currentOption.linkType} />
						<Box variant="span">Link</Box>
						<Input name="Link" autoComplete="off" inputMode="url" type="text" disableBrowserAutocorrect={true} placeholder="Link" value={bannersEditModal.link} onChange={(event) => this.updateParam("link", event.detail.value)} />
						<Box variant="span">Description Type</Box>
						<Select filteringType="auto" placeholder="Select description type" selectedOption={bannersEditModal.descriptionType} onChange={(option) => this.updateParam("descriptionType", option.detail.selectedOption)} options={bannersEditModal.currentOption.descriptionType} />
						<Grid disableGutters gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
							<Box variant="span" margin={{ right: "xxxs" }}>
								Description
							</Box>
							<SpaceBetween direction="horizontal">
								<Box margin={{ right: "xs" }}>Personalization</Box>
								<Checkbox
									checked={bannersEditModal.personalization}
									onChange={({ detail }) => {
										this.updateParam("personalization", detail.checked);
									}}
								/>
							</SpaceBetween>
						</Grid>
						<Input name="Description" autoComplete="off" inputMode="text" type="text" disableBrowserAutocorrect={true} placeholder="Description" value={bannersEditModal.description} onChange={(event) => this.updateParam("description", event.detail.value)} />

						{bannersEditModal.personalization && (
							<>
								<Box variant="span">Default Description Type</Box>
								<Select filteringType="auto" placeholder="Select default description type" selectedOption={bannersEditModal.descriptionDefaultType} onChange={(option) => this.updateParam("descriptionDefaultType", option.detail.selectedOption)} options={bannersEditModal.currentOption.descriptionDefaultType} />

								<Box variant="span">Default Description</Box>
								<Input
									name="descriptionDefault"
									autoComplete="off"
									inputMode="text"
									type="text"
									disableBrowserAutocorrect={true}
									placeholder="Default Description"
									value={bannersEditModal.descriptionDefault}
									onChange={(event) => {
										this.updateParam("descriptionDefault", event.detail.value);
									}}
								/>
							</>
						)}

						<Box variant="span">Badge Type</Box>
						<Select filteringType="auto" placeholder="Select badge type" selectedOption={bannersEditModal.badgeType} onChange={(option) => this.updateParam("badgeType", option.detail.selectedOption)} options={bannersEditModal.currentOption.badgeType} />
						<Box variant="span">Badge</Box>
						<Input name="Badge" autoComplete="off" inputMode="text" type="text" disableBrowserAutocorrect={true} placeholder="Badge" value={bannersEditModal.badge} onChange={(event) => this.updateParam("badge", event.detail.value)} />
						<Box variant="span">Analytics ID</Box>
						<Input name="AnalyticsID" autoComplete="off" inputMode="text" type="text" disableBrowserAutocorrect={true} placeholder="Analytics ID" value={bannersEditModal.analyticsID} onChange={(event) => this.updateParam("analyticsID", event.detail.value)} />
						<SpaceBetween direction="horizontal" size="l">
							<Toggle
								checked={String(bannersEditModal.active?.value ?? bannersEditModal.active) === "1"}
								onChange={({ detail }) =>
									this.updateParam("active", {
										value: detail.checked ? "1" : "0",
										label: detail.checked ? "1" : "0",
									})
								}>
								{String(bannersEditModal.active?.value ?? bannersEditModal.active) === "1" ? "Active" : "Inactive"}
							</Toggle>
							<Toggle checked={normalizeRequireAuthValue(bannersEditModal.requireAuth) === "1"} onChange={({ detail }) => this.updateParam("requireAuth", detail.checked ? "1" : "0")}>
								{normalizeRequireAuthValue(bannersEditModal.requireAuth) === "1" ? "Required auth" : "Not required auth"}
							</Toggle>
						</SpaceBetween>
						<SpaceBetween direction="horizontal" size="l">
							<Checkbox checked={bannersEditModal.requireTg} onChange={({ detail }) => this.updateParam("requireTg", detail.checked)}>
								Skip link TG
							</Checkbox>
							<Checkbox checked={bannersEditModal.requireEmail} onChange={({ detail }) => this.updateParam("requireEmail", detail.checked)}>
								Skip link Email
							</Checkbox>
						</SpaceBetween>
						<Box variant="span">Order</Box>
						<Input name="Order" autoComplete="off" inputMode="number" type="number" disableBrowserAutocorrect={true} placeholder="Order" value={bannersEditModal.order} onChange={(event) => this.updateParam("order", event.detail.value)} />
						<Box variant="span">From/to</Box>
						<DateRangePicker
							onChange={({ detail }) => {
								this.updateParam("from", detail.value.startDate.split("+")[0]);
								this.updateParam("to", detail.value.endDate.split("+")[0]);
								this.updateParam("type", detail.value.type);

								setBannersEditModal({
									...bannersEditModal,
									from: detail.value.startDate.split("+")[0],
									to: detail.value.endDate.split("+")[0],
									type: detail.value.type,
								});
							}}
							value={{
								startDate: bannersEditModal.from,
								endDate: bannersEditModal.to,
								type: "absolute",
							}}
							isValidRange={(range) => {
								if (range.type === "absolute") {
									const [startDateWithoutTime] = range.startDate.split("T");
									const [endDateWithoutTime] = range.endDate.split("T");
									if (!startDateWithoutTime || !endDateWithoutTime) {
										return {
											valid: false,
											errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
										};
									}
									if (new Date(range.startDate) - new Date(range.endDate) > 0) {
										return {
											valid: false,
											errorMessage: "The selected date range is invalid. The start date must be before the end date.",
										};
									}
								} else {
									return { valid: false };
								}
							}}
							i18nStrings={{
								todayAriaLabel: "Today",
								nextMonthAriaLabel: "Next month",
								previousMonthAriaLabel: "Previous month",
								customRelativeRangeDurationLabel: "Duration",
								customRelativeRangeDurationPlaceholder: "Enter duration",
								customRelativeRangeOptionLabel: "Custom range",
								customRelativeRangeOptionDescription: "Define a custom range in the past",
								customRelativeRangeUnitLabel: "Unit of time",
								formatRelativeRange: ({ amount, unit, startDate, endDate }) => {
									const start = new Date(startDate);
									const end = new Date(endDate);

									const locale = "en-GB";
									const dateOptions = { year: "numeric", month: "long", day: "numeric" };
									const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

									const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
									const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

									return `${formattedStart} - ${formattedEnd}`;
								},
								formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
								dateTimeConstraintText: "Date and time must be within the selected range.",
								renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
								labelRelativeRange: "Relative range",
								labelAbsoluteRange: "Absolute range",
								dateTimePickerAriaLabel: "Date and time picker",
								dateTimePickerLabel: "Date and Time",
								clearButtonAriaLabel: "Clear date range",
								relativeModeTitle: "Relative mode",
								absoluteModeTitle: "Absolute mode",
								cancelButtonLabel: "Cancel",
								applyButtonLabel: "Apply",
							}}
							placeholder="From/to"
						/>
					</SpaceBetween>
				}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="m">
							<Button variant="normal" onClick={() => setBannersEditModal({ ...bannersEditModal, show: false })} children="Close" />
							<Button variant="primary" onClick={() => this.editBanner()} disabled={!isDirty} children="Ok" />
						</SpaceBetween>
					</Box>
				}
			/>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const bannersEditModal = useRecoilValue(bannersEditModalStore);
		const setBannersEditModal = useSetRecoilState(bannersEditModalStore);
		const banners = useRecoilValue(bannersStore);
		const setBanners = useSetRecoilState(bannersStore);
		return <Component {...props} banners={banners} setBanners={setBanners} setBannersEditModal={setBannersEditModal} bannersEditModal={bannersEditModal} />;
	};
}

export default withRecoil(BannersEditModal);
